import { T } from '@/i18n/LocaleProvider';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import PersonPhoto from './PersonPhoto';
import PersonCardLinks from './PersonCardLinks';
import { MONO_FONT } from '@/theme';

// Difumina el borde del recorte hacia el fondo (evita el look "pegado"):
// disuelve fuerte el inferior y suaviza los costados, manteniendo nítida la cara.
const FADE_MASK =
  'radial-gradient(120% 92% at 50% 34%, #000 58%, rgba(0,0,0,0.55) 78%, transparent 100%)';

export default function PersonCard({
  name,
  role,
  credential,
  bio,
  initials,
  icon,
  image,
  imageAlt,
  imageBg = '#f2f4f3',
  imageFade = false,
  linkedin,
  profileHref,
}) {
  const fallbackInitials =
    initials ??
    name
      ?.split(' ')
      .map((w) => w[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();

  return (
    <Box
      component="article"
      sx={{
        border: '1px solid',
        borderColor: 'grey.200',
        p: { xs: '20px', md: '28px' },
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      {image ? (
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            aspectRatio: '1 / 1',
            overflow: 'hidden',
            borderRadius: 1,
            background: imageBg,
          }}
        >
          <PersonPhoto
            src={image}
            name={name}
            role={role}
            alt={imageAlt}
            fill
            sizes="(max-width: 600px) 50vw, (max-width: 900px) 33vw, 360px"
            style={{
              objectFit: 'cover',
              objectPosition: 'center top',
              ...(imageFade
                ? { maskImage: FADE_MASK, WebkitMaskImage: FADE_MASK }
                : {}),
            }}
          />
        </Box>
      ) : icon ? (
        <Box
          aria-hidden
          sx={{
            width: '56px',
            height: '56px',
            bgcolor: 'primary.lightest',
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'text.accent',
            flexShrink: 0,
            '& svg': { width: 32, height: 32 },
          }}
        >
          {icon}
        </Box>
      ) : (
        <Box
          sx={{
            width: '56px',
            height: '56px',
            bgcolor: 'grey.100',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: MONO_FONT,
            fontSize: '14px',
            fontWeight: 500,
            color: 'grey.600',
            letterSpacing: '0.05em',
            flexShrink: 0,
          }}
        >
          {fallbackInitials}
        </Box>
      )}

      <Box>
        <Typography
          sx={{
            fontSize: '17px',
            fontWeight: 500,
            letterSpacing: '-0.01em',
            color: 'text.primary',
            lineHeight: 1.2,
          }}
        >
          <T>{name}</T>
        </Typography>
        {role && (
          <Typography
            sx={{
              fontFamily: MONO_FONT,
              fontSize: '11.5px',
              fontWeight: '500',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'text.accent',
              mt: 1,
            }}
          >
            <T>{role}</T>
          </Typography>
        )}
        {credential && (
          <Typography
            sx={{
              fontFamily: MONO_FONT,
              fontSize: '10.5px',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: 'text.muted',
              mt: 0.5,
            }}
          >
            <T>{credential}</T>
          </Typography>
        )}
      </Box>

      {bio && (
        <Typography
          sx={{
            fontSize: '14px',
            lineHeight: 1.7,
            color: 'grey.600',
          }}
        >
          <T>{bio}</T>
        </Typography>
      )}

      {(profileHref || linkedin) && (
        <PersonCardLinks
          name={name}
          profileHref={profileHref}
          linkedin={linkedin}
        />
      )}
    </Box>
  );
}
